import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { brokerageAPI } from '../../services/api';
import { useBranchFilter } from '../../contexts/BranchFilterContext';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import './BrokerageList.css';

const BrokerageList = () => {
  const { t } = useTranslation();
  const { selectedBranchId } = useBranchFilter();
  const [brokerages, setBrokerages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    loadBrokerages();
  }, [page, selectedBranchId]);

  const loadBrokerages = async () => {
    try {
      setLoading(true);
      const response = await brokerageAPI.getAll(searchTerm, page, pageSize, selectedBranchId);
      setBrokerages(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      setErrorMessage(t('brokerage.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadBrokerages();
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await brokerageAPI.delete(id);
        setSuccessMessage(t('brokerage.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadBrokerages();
      } catch (err) {
        setErrorMessage(t('brokerage.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('brokerage.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('brokerage.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await brokerageAPI.bulkDelete(selectedIds);
        setSuccessMessage(response.data.message || t('brokerage.list.messages.bulkDeleteSuccess', { count: selectedIds.length }));
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadBrokerages();
      } catch (err) {
        setErrorMessage(err.response?.data?.message || t('brokerage.list.messages.bulkDeleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('brokerage.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) {
      await confirmAction();
    }
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(brokerages.map(b => b.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExport = async (format) => {
    try {
      setShowExportMenu(false);
      let response;
      let filename;
      let contentType;

      switch (format) {
        case 'excel':
          response = await brokerageAPI.exportExcel();
          filename = `Brokerages_${new Date().toISOString().slice(0, 10)}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await brokerageAPI.exportCsv();
          filename = `Brokerages_${new Date().toISOString().slice(0, 10)}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await brokerageAPI.exportPdf();
          filename = `Brokerages_${new Date().toISOString().slice(0, 10)}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      const blob = new Blob([response.data], { type: contentType });
      saveAs(blob, filename);
      setSuccessMessage(t('brokerage.list.messages.exportSuccess', { format: format.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t('brokerage.list.messages.exportError', { format: format.toUpperCase() }) + ` ${err.response?.data?.message || err.message}`);
      setShowErrorModal(true);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await brokerageAPI.downloadTemplate();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `BrokeragesTemplate_${new Date().toISOString().slice(0, 10)}.xlsx`);
      setSuccessMessage(t('brokerage.list.messages.templateDownloadSuccess'));
      setShowSuccessModal(true);
    } catch (err) {
      setErrorMessage(t('brokerage.list.messages.templateDownloadError'));
      setShowErrorModal(true);
    }
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setErrorMessage(t('brokerage.list.messages.importError'));
      setShowErrorModal(true);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await brokerageAPI.bulkUpload(formData);
      
      if (response.data.success) {
        setSuccessMessage(response.data.message || t('brokerage.list.messages.importSuccess', { count: response.data.count || 0 }));
        setShowSuccessModal(true);
        loadBrokerages();
      } else {
        setErrorMessage(response.data.message || t('brokerage.list.messages.importFailed'));
        setShowErrorModal(true);
      }

      e.target.value = '';
    } catch (err) {
      setErrorMessage(t('brokerage.list.messages.importProcessError'));
      setShowErrorModal(true);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="list-view-card">
        <div className="page-actions">
          <div className="page-actions-left">
            <Link to="/brokerage/create" className="btn-add">
              + {t('brokerage.list.addNew')}
            </Link>
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder={t('brokerage.list.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              />
              <button className="search-icon" onClick={handleSearch}>
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>
          <div className="action-buttons">
            <div className="export-menu-container">
              <button className="action-btn action-btn-export" title={t('common.export')} onClick={() => setShowExportMenu(!showExportMenu)}>
                <i className="fas fa-file-export"></i>
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button onClick={() => handleExport('excel')}>
                    <i className="fas fa-file-excel"></i> {t('common.exportExcel')}
                  </button>
                  <button onClick={() => handleExport('csv')}>
                    <i className="fas fa-file-csv"></i> {t('common.exportCsv')}
                  </button>
                  <button onClick={() => handleExport('pdf')}>
                    <i className="fas fa-file-pdf"></i> {t('common.exportPdf')}
                  </button>
                </div>
              )}
            </div>
            <button className="action-btn action-btn-import" title={t('brokerage.list.import')} onClick={handleImport}>
              <i className="fas fa-file-import"></i>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
            <button className="action-btn action-btn-download" title={t('brokerage.list.downloadTemplate')} onClick={handleDownloadTemplate}>
              <i className="fas fa-download"></i>
            </button>
            <button className="action-btn action-btn-delete" title={t('brokerage.list.bulkDelete')} onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />

      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title}
        message={confirmData?.message}
        confirmText={confirmData?.confirmText}
        cancelText={confirmData?.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedIds.length === brokerages.length && brokerages.length > 0}
                />
              </th>
              <th>{t('brokerage.list.columns.id')}</th>
              <th>{t('brokerage.list.columns.date')}</th>
              <th>{t('brokerage.list.columns.unitLocation')}</th>
              <th>{t('brokerage.list.columns.ownerName')}</th>
              <th>{t('brokerage.list.columns.commissionAmount')}</th>
              <th>{t('brokerage.list.columns.brokerName')}</th>
              <th>{t('brokerage.list.columns.notes')}</th>
              <th>{t('brokerage.list.columns.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {brokerages.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-4">
                  {t('brokerage.list.messages.noData')}
                </td>
              </tr>
            ) : (
              brokerages.map((brokerage) => (
                <tr key={brokerage.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(brokerage.id)}
                      onChange={() => handleSelectOne(brokerage.id)}
                    />
                  </td>
                  <td>{brokerage.id}</td>
                  <td>{new Date(brokerage.date).toLocaleDateString()}</td>
                  <td>{brokerage.unitLocation}</td>
                  <td>{brokerage.ownerName}</td>
                  <td>{parseFloat(brokerage.commissionAmount).toFixed(2)}</td>
                  <td>{brokerage.brokerName}</td>
                  <td>{brokerage.notes || '-'}</td>
                  <td>
                    <div className="action-buttons-cell">
                      <Link
                        to={`/brokerage/view/${brokerage.id}`}
                        className="btn btn-sm btn-view"
                        title={t('common.view')}
                      >
                        <i className="fas fa-eye"></i>
                      </Link>
                      <Link
                        to={`/brokerage/edit/${brokerage.id}`}
                        className="btn btn-sm btn-edit"
                        title={t('common.edit')}
                      >
                        <i className="fas fa-edit"></i>
                      </Link>
                      <button
                        className="btn btn-sm btn-delete"
                        onClick={() => handleDelete(brokerage.id)}
                        title={t('common.delete')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination-container">
          <ul className="pagination">
            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                {t('common.previous') || 'Previous'}
              </button>
            </li>
            {[...Array(totalPages)].map((_, i) => (
              <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => setPage(i + 1)}>
                  {i + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                {t('common.next')}
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default BrokerageList;

